/**
 * NATURAH THEME - Product Recommendations
 * Fetches and displays recommended products using Shopify Recommendations API
 * File: assets/product-recommendations.js
 * Version: 1.0.0
 *
 * Expected Impact: +8-12% average order value through upselling
 */

class ProductRecommendations {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.grid = container.querySelector('[data-recommendations-grid]');
    this.loading = container.querySelector('[data-recommendations-loading]');
    this.empty = container.querySelector('[data-recommendations-empty]');

    // Get section settings from the container
    this.settings = this.getSettings();

    if (this.productId && this.grid) {
      this.init();
    }
  }

  getSettings() {
    // Try to get settings from section schema, fallback to defaults
    const section = this.container.closest('.section-product-recommendations');
    return {
      productsToShow: 4,
      showVendor: false,
      showQuickAdd: true
    };
  }

  async init() {
    try {
      const recommendations = await this.fetchRecommendations();

      if (recommendations && recommendations.length > 0) {
        this.renderRecommendations(recommendations);
      } else {
        this.showEmptyState();
      }
    } catch (error) {
      console.error('Failed to load product recommendations:', error);
      this.showEmptyState();
    }
  }

  async fetchRecommendations() {
    const limit = this.settings.productsToShow || 4;
    const url = `/recommendations/products.json?product_id=${this.productId}&limit=${limit}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return [];
    }
  }

  renderRecommendations(products) {
    // Hide loading skeleton
    if (this.loading) {
      this.loading.hidden = true;
    }

    // Clear grid and render products
    this.grid.innerHTML = '';

    products.forEach(product => {
      const card = this.createProductCard(product);
      this.grid.appendChild(card);
    });

    // Show the section
    this.container.style.display = 'block';

    // Initialize quick add for the new cards
    if (window.QuickAdd) {
      new window.QuickAdd();
    }
  }

  createProductCard(product) {
    const article = document.createElement('article');
    article.className = 'product-card product-card--recommendation';

    // Stretched overlay link — this MUST mirror the markup contract in
    // snippets/product-card.liquid (#137): `.product-card__link` is
    // `position: absolute; inset: 0`, so it has to be an EMPTY sibling of the media and
    // the copy, never their wrapper. Wrapping them (as this file used to) leaves the
    // article with no in-flow children, so every recommendation card collapses to
    // height 0 and the section paints as a bare heading. It also put the Quick Add
    // <button> inside an <a>, which is invalid HTML. The title rides along as
    // screen-reader text so the empty anchor still has an accessible name.
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'product-card__link';

    const linkLabel = document.createElement('span');
    linkLabel.className = 'product-card__sr-only';
    linkLabel.textContent = product.title;
    link.appendChild(linkLabel);

    // Image section
    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'product-card__image-wrapper';

    if (product.featured_image) {
      const img = document.createElement('img');
      img.src = this.getImageUrl(product.featured_image, 600);
      img.alt = product.title;
      img.className = 'product-card__image';
      img.loading = 'lazy';
      imageWrapper.appendChild(img);
    }

    // Badge for sale or sold out
    if (!product.available) {
      const badge = document.createElement('span');
      badge.className = 'product-card__badge product-card__badge--sold-out';
      badge.textContent = 'Sold Out';
      imageWrapper.appendChild(badge);
    } else if (product.compare_at_price && product.compare_at_price > product.price) {
      const badge = document.createElement('span');
      badge.className = 'product-card__badge product-card__badge--sale';
      const savings = Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
      badge.textContent = `Save ${savings}%`;
      imageWrapper.appendChild(badge);
    }

    // Quick add overlay (if enabled and product is available)
    if (this.settings.showQuickAdd && product.available && product.variants && product.variants.length > 0) {
      const overlay = document.createElement('div');
      overlay.className = 'product-card__overlay';

      const quickAddBtn = document.createElement('button');
      quickAddBtn.className = 'product-card__quick-add btn btn--primary btn--small';
      quickAddBtn.dataset.productId = product.id;
      quickAddBtn.dataset.variantId = product.variants[0].id;
      quickAddBtn.dataset.quickAddBtn = '';
      quickAddBtn.setAttribute('aria-live', 'polite');
      quickAddBtn.setAttribute('aria-atomic', 'true');

      const btnText = document.createElement('span');
      btnText.className = 'product-card__quick-add-text';
      btnText.dataset.quickAddText = '';
      btnText.textContent = 'Quick Add';

      quickAddBtn.appendChild(btnText);

      overlay.appendChild(quickAddBtn);
      imageWrapper.appendChild(overlay);
    }

    // Content section
    const content = document.createElement('div');
    content.className = 'product-card__content';

    // Title
    const title = document.createElement('h3');
    title.className = 'product-card__title';
    title.textContent = product.title;
    content.appendChild(title);

    // Vendor (if enabled)
    if (this.settings.showVendor && product.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'product-card__vendor';
      vendor.textContent = product.vendor;
      content.appendChild(vendor);
    }

    // Price
    const priceDiv = document.createElement('div');
    priceDiv.className = 'product-card__price';

    if (product.compare_at_price && product.compare_at_price > product.price) {
      const salePrice = document.createElement('span');
      salePrice.className = 'product-card__price--sale';
      salePrice.textContent = this.formatMoney(product.price);
      priceDiv.appendChild(salePrice);

      const comparePrice = document.createElement('span');
      comparePrice.className = 'product-card__price--compare';
      comparePrice.textContent = this.formatMoney(product.compare_at_price);
      priceDiv.appendChild(comparePrice);
    } else {
      const price = document.createElement('span');
      price.textContent = this.formatMoney(product.price);
      priceDiv.appendChild(price);
    }

    content.appendChild(priceDiv);

    // Variant count
    if (product.variants && product.variants.length > 1) {
      const meta = document.createElement('div');
      meta.className = 'product-card__meta';

      const variantCount = document.createElement('span');
      variantCount.className = 'product-card__variant-count';
      variantCount.textContent = `${product.variants.length} variants`;

      meta.appendChild(variantCount);
      content.appendChild(meta);
    }

    // Overlay first (it is out of flow), then the two elements that give the card its
    // height — same order as the snippet, so both consumers of `.product-card__link`
    // build the same shape.
    article.appendChild(link);
    article.appendChild(imageWrapper);
    article.appendChild(content);

    return article;
  }

  getImageUrl(imageUrl, width) {
    // Shopify CDN image resizing
    if (imageUrl.includes('cdn.shopify.com')) {
      const extension = imageUrl.split('.').pop();
      return imageUrl.replace(`.${extension}`, `_${width}x.${extension}`);
    }
    return imageUrl;
  }

  formatMoney(cents) {
    // Simple money formatting - adjust based on shop currency settings
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  showEmptyState() {
    // Hide loading skeleton
    if (this.loading) {
      this.loading.hidden = true;
    }

    // Hide the entire section if no recommendations
    // Alternative: show empty state message
    this.container.style.display = 'none';

    // Uncomment below to show empty message instead of hiding section
    // if (this.empty) {
    //   this.empty.hidden = false;
    // }
  }
}

// Initialize all recommendation sections
function initRecommendations() {
  const containers = document.querySelectorAll('[data-product-recommendations]');
  containers.forEach(container => {
    new ProductRecommendations(container);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecommendations);
} else {
  initRecommendations();
}

// Export for use by other scripts
window.ProductRecommendations = ProductRecommendations;
